import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Phone, ArrowDown } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { heroImg } from "@/data/content";
import { PHONE_HREF } from "@/lib/site";

const lineVariants = {
  hidden: { y: "115%" },
  show: (i: number) => ({
    y: "0%",
    transition: { duration: 1, ease: [0.22, 1, 0.36, 1] as const, delay: 0.35 + i * 0.14 },
  }),
};

export const Hero = () => {
  const { t } = useLanguage();
  const ref = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.75], [1, 0]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);

  const lines = [t.hero.line1, t.hero.line2, t.hero.line3];

  return (
    <section
      ref={ref}
      id="top"
      data-testid="hero-section"
      className="relative flex min-h-[100svh] flex-col justify-end overflow-hidden bg-ink"
    >
      <motion.div style={{ y: bgY }} className="absolute inset-0 scale-110">
        <motion.img
          src={heroImg}
          alt="Drywall construction framing on a Houston job site"
          initial={{ scale: 1.18, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 1.8, ease: [0.22, 1, 0.36, 1] }}
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-ink/60" />
        <div className="absolute inset-x-0 bottom-0 h-56 bg-ink/80 [mask-image:linear-gradient(to_top,black,transparent)]" />
      </motion.div>

      <motion.div
        style={{ opacity: contentOpacity, y: contentY }}
        className="relative z-10 mx-auto w-full max-w-[1600px] px-5 pb-14 pt-40 md:px-10 md:pb-20"
      >
        <motion.p
          data-testid="hero-eyebrow"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="mb-6 flex items-center gap-3 text-[11px] font-bold uppercase tracking-[0.3em] text-gold md:text-xs"
        >
          <span className="inline-block h-[2px] w-10 bg-gold" />
          {t.hero.eyebrow}
        </motion.p>

        <h1
          data-testid="hero-title"
          className="font-display text-[11vw] font-black uppercase leading-[0.95] tracking-tight sm:text-[10vw] lg:text-[8.5vw]"
        >
          <span className="block overflow-hidden pb-1">
            <motion.span
              custom={0}
              variants={lineVariants}
              initial="hidden"
              animate="show"
              className="block text-white"
            >
              {lines[0]}
            </motion.span>
          </span>
          <span className="block overflow-hidden pb-1">
            <motion.span
              custom={1}
              variants={lineVariants}
              initial="hidden"
              animate="show"
              className="text-stroke block"
            >
              {lines[1]}
            </motion.span>
          </span>
          <span className="block overflow-hidden pb-2">
            <motion.span
              custom={2}
              variants={lineVariants}
              initial="hidden"
              animate="show"
              className="block text-gold"
            >
              {lines[2]}
            </motion.span>
          </span>
        </h1>

        <motion.p
          data-testid="hero-tagline"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1 }}
          className="mt-6 text-sm font-bold uppercase tracking-[0.2em] text-neutral-300 md:text-base"
        >
          {t.hero.tagline}
        </motion.p>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.1 }}
          className="mt-3 max-w-xl text-sm leading-relaxed text-neutral-400 md:text-base"
        >
          {t.hero.sub}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.25 }}
          className="mt-9 flex flex-wrap items-center gap-4"
        >
          <Link
            data-testid="hero-request-bid-btn"
            to="/request-a-bid"
            className="group flex items-center gap-2 rounded-full bg-gold px-8 py-4 text-sm font-black uppercase tracking-[0.12em] text-ink transition-colors duration-300 hover:bg-golddark"
          >
            {t.hero.ctaPlans}
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
          </Link>
          <a
            data-testid="hero-call-btn"
            href={PHONE_HREF}
            className="group flex items-center gap-2 rounded-full border border-white/25 px-8 py-4 text-sm font-bold text-white transition-colors duration-300 hover:border-gold hover:text-gold"
          >
            <Phone className="h-4 w-4" />
            {t.hero.ctaCall}
          </a>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-8 right-5 z-10 hidden items-center gap-3 md:right-10 md:flex"
        data-testid="hero-scroll-hint"
      >
        <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-neutral-400">
          {t.hero.scroll}
        </span>
        <motion.span
          animate={{ y: [0, 6, 0] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
        >
          <ArrowDown className="h-4 w-4 text-gold" />
        </motion.span>
      </motion.div>
    </section>
  );
};
